import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BellRinging, PaperPlaneTilt, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Settings() {
  const [s, setS] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  useEffect(() => { api.get("/currencies").then((r) => setCurrencies(r.data)); }, []);

  const set = (k, v) => setS((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        reminders_enabled: s.reminders_enabled,
        notify_email: s.notify_email,
        lease_end_days: parseInt(s.lease_end_days) || 30,
        payment_due_days: parseInt(s.payment_due_days) || 7,
        two_step_enabled: s.two_step_enabled,
        two_step_threshold: parseFloat(s.two_step_threshold) || 0,
        two_step_threshold_currency: s.two_step_threshold_currency || "USD",
      });
      setS(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/run");
      if (data.skipped) toast.info(`Reminders skipped: ${data.skipped}`);
      else toast.success(`Reminders processed — ${data.sent.lease_end} ending, ${data.sent.payment_due} payment(s) emailed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (!s) return <div className="text-muted-foreground text-sm">Loading settings…</div>;

  return (
    <div className="space-y-8 max-w-2xl">
      <div className="fade-up">
        <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-2">Preferences</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Settings</h1>
      </div>

      <Card className="rounded-sm border-border shadow-none p-6 bg-card fade-up">
        <div className="flex items-center gap-2 mb-6">
          <BellRinging size={20} className="text-primary" />
          <h2 className="font-display text-lg font-semibold tracking-tight">Renewal & Payment Reminders</h2>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Email reminders</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Get an email before a lease ends or a payment falls due.</p>
            </div>
            <Switch checked={s.reminders_enabled} onCheckedChange={(v) => set("reminders_enabled", v)} data-testid="reminders-enabled" />
          </div>

          <div>
            <Label>Notification email</Label>
            <Input type="email" value={s.notify_email || ""} onChange={(e) => set("notify_email", e.target.value)}
              className="mt-1.5 rounded-sm" data-testid="notify-email" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Notify before lease ends (days)</Label>
              <Input type="number" min="1" max="365" value={s.lease_end_days} onChange={(e) => set("lease_end_days", e.target.value)}
                className="mt-1.5 rounded-sm font-mono" data-testid="lease-end-days" />
            </div>
            <div>
              <Label>Notify before payment due (days)</Label>
              <Input type="number" min="1" max="180" value={s.payment_due_days} onChange={(e) => set("payment_due_days", e.target.value)}
                className="mt-1.5 rounded-sm font-mono" data-testid="payment-due-days" />
            </div>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border-border shadow-none p-6 bg-card fade-up">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck size={20} className="text-primary" />
          <h2 className="font-display text-lg font-semibold tracking-tight">Payment Approval Controls</h2>
        </div>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Two-step approval for high-value payments</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Maker-checker: payments at or above the threshold need a second approval by a different user.</p>
            </div>
            <Switch checked={s.two_step_enabled} onCheckedChange={(v) => set("two_step_enabled", v)} data-testid="two-step-enabled" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Threshold amount</Label>
              <Input type="number" min="0" value={s.two_step_threshold} onChange={(e) => set("two_step_threshold", e.target.value)}
                className="mt-1.5 rounded-sm font-mono" data-testid="two-step-threshold" disabled={!s.two_step_enabled} />
            </div>
            <div>
              <Label>Threshold currency</Label>
              <Select value={s.two_step_threshold_currency} onValueChange={(v) => set("two_step_threshold_currency", v)} disabled={!s.two_step_enabled}>
                <SelectTrigger className="mt-1.5 rounded-sm font-mono" data-testid="two-step-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Payment amounts are converted to the threshold currency (live FX) before comparison.</p>
        </div>
      </Card>

      <div className="flex items-center gap-3 fade-up">
        <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="save-settings-btn">
          {saving ? "Saving…" : "Save Settings"}
        </Button>
        <Button onClick={runNow} disabled={sending} variant="outline" className="rounded-sm gap-2" data-testid="run-reminders-btn">
          <PaperPlaneTilt size={16} /> {sending ? "Sending…" : "Send reminders now"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground -mt-4">
        Reminders run automatically every day at 08:00 UTC. Each reminder is sent once — you won't get duplicates.
      </p>
    </div>
  );
}
